<template>
  <v-form>
    <div class="mb-4">
      <v-row dense>
        <v-col cols="12" sm="6">
          <px-autocomplete-comprador
            dense
            required
            label="Local de faturamento"
            class="required rounded-tag-small"
            v-disable-update-if-blocked="{
              field: 'comprador',
              bloqueios: camposBloqueadosEdicao,
              isEdicao: isEdicao,
            }"
            :error="$v.requisicao.comprador.$error"
            @blur="$v.requisicao.comprador.$touch()"
            v-model="requisicao.comprador"/>
        </v-col>
        <v-col cols="12" sm="6">
          <px-autocomplete-locais-entrega
            dense
            required
            class="required rounded-tag-small"
            :error="$v.requisicao.locaisEntrega.$error"
            @blur="$v.requisicao.locaisEntrega.$touch()"
            v-model="requisicao.locaisEntrega"/>
        </v-col>
      </v-row>
      <v-row dense v-if="!isEdicao || isRascunho">
        <v-col cols="12" sm="12">
          <px-autocomplete-user-comprador-multiple
            dense
            fetch-on-focus
            label="Compradores responsáveis"
            :roles="['ROLE_COMPRADOR']"
            :class="`rounded-tag-small ${isCompradoresResponsaveisRequired ? 'required' : ''}`"
            :error="$v.requisicao.compradoresResponsaveis.$error"
            @blur="$v.requisicao.compradoresResponsaveis.$touch()"
            v-model="requisicao.compradoresResponsaveis"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="4">
          <px-select-enum dense
                          label="Urgência"
                          class="rounded-tag-small required"
                          v-model="requisicao.urgencia"
                          :error="$v.requisicao.urgencia.$error"
                          @blur="$v.requisicao.urgencia.$touch()"
                          :items="urgencias"/>
        </v-col>
        <v-col cols="12" sm="4">
          <px-date-picker dense
                          label="Data de Entrega"
                          input-class="rounded-tag-small required"
                          v-model="requisicao.dataEntrega"
                          :error="$v.requisicao.dataEntrega.$error"
                          @blur="$v.requisicao.dataEntrega.$touch()"/>
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            dense
            single-line
            outlined
            hide-details
            maxlength="50"
            class="rounded-tag-small"
            label="Identificador"
            v-model="requisicao.identificador"
          >
            <template v-slot:prepend-inner>
              <span class="pt-1">{{ identificador }}</span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="6">
          <px-autocomplete-projeto
            dense
            :class="`rounded-tag-small ${isProjetoRequired ? 'required' : ''}`"
            :error="$v.requisicao.projeto.$error"
            @blur="$v.requisicao.projeto.$touch()"
            v-model="requisicao.projeto"/>
        </v-col>
        <v-col cols="12" sm="6">
          <px-autocomplete-centro-custo
            dense
            :class="`rounded-tag-small ${isCentroCustoRequired ? 'required' : ''}`"
            :error="$v.requisicao.centroCusto.$error"
            @blur="$v.requisicao.centroCusto.$touch()"
            v-model="requisicao.centroCusto"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            dense
            outlined
            auto-grow
            hide-details
            label="Observações internas"
            rows="3"
            class="rounded-tag-small"
            v-model="requisicao.observacaoInterna"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            dense
            outlined
            auto-grow
            label="Observações externas"
            counter="1500"
            rows="3"
            class="rounded-tag-small"
            v-model="requisicao.observacaoExterna">
            <template v-slot:append-outer>
              <px-anexo v-model="requisicao.arquivos"
                        :max="5">
              </px-anexo>
            </template>
          </v-textarea>
        </v-col>
      </v-row>
       <px-campos-integracao dense
                            type="requisicao"
                            :form-data="requisicao.cadastrosAdicionais"/>
      <v-row dense>
        <v-col cols="12">
          <div class="font-weight-bold body-2">
            Finalidade da compra
          </div>
          <v-radio-group
            row
            v-model="requisicao.finalidadeCompra"
            class="d-flex"
          >
            <v-radio
              v-for="finalidade in finalidades"
              :key="finalidade.key"
              :label="finalidade.description"
              :value="finalidade.key"
            ></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-btn class="rounded-tag-small normal-btn-text elevation-0"
                 color="primary"
                 @click="next">
            Continuar
          </v-btn>
          <v-btn class="normal-btn-text"
                 text
                 @click="onClickCancelar">
            Cancelar
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex';
import { FinalidadeCompra, UrgenciaRequisicao } from 'px-business-components';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { isFieldRequiredEntity } from '@/modules/erp-campos/erp-campos-utils';
import validationMixin from '@/mixins/validation-mixin';
import { isDateAfterOrSameToday } from '@/utils/date-utils';
import { validateCamposErp } from './campos-erp/requisicao/validate-campos-erp';

export default {
  mixins: [validationMixin],
  props: {
    requisicao: {
      type: Object,
      required: true,
    },
    isEdicao: Boolean,
    isRascunho: Boolean,
    camposObrigatorios: Object,
    camposBloqueadosEdicao: Object,
  },
  data() {
    return {
      finalidades: FinalidadeCompra.values,
      urgencias: UrgenciaRequisicao.values,
    };
  },
  computed: {
    ...mapGetters('entidade', ['entidade', 'erpCamposIntegracao']),
    identificador() {
      return this.entidade ? this.entidade.identificador : '';
    },
    isCentroCustoRequired() {
      return isFieldRequiredEntity('centroCusto', this.camposObrigatorios);
    },
    isProjetoRequired() {
      return isFieldRequiredEntity('projeto', this.camposObrigatorios);
    },
    isCompradoresResponsaveisRequired() {
      return isFieldRequiredEntity('compradoresResponsaveis', this.camposObrigatorios);
    },
  },
  methods: {
    next() {
      if (this.validateForm() || !this.validateCadastrosAdicionais()) {
        return;
      }
      this.$emit('next');
    },
    onClickCancelar() {
      this.$emit('cancel');
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(this.erpCamposIntegracao,
        this.requisicao.cadastrosAdicionais);
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
  },
  validations: {
    requisicao: {
      comprador: {
        required,
      },
      locaisEntrega: {
        required,
      },
      urgencia: {
        required,
      },
      dataEntrega: {
        required,
        customDataValidation(value) {
          if (this.isEdicao) {
            return true;
          }
          return isDateAfterOrSameToday(value);
        },
      },
      centroCusto: {
        required: requiredIf(function () {
          return this.isCentroCustoRequired;
        }),
      },
      projeto: {
        required: requiredIf(function () {
          return this.isProjetoRequired;
        }),
      },
      compradoresResponsaveis: {
        required: requiredIf(function () {
          return this.isCompradoresResponsaveisRequired;
        }),
      },
    },
  },
};
</script>
